import React, {useState, useEffect} from 'react';
import {connect} from 'react-redux';
import {withStyles} from '@material-ui/core';
import {styles, Classes} from './styles';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Autosuggest from 'react-autosuggest';
import UserInfo from 'feedback-system/components/user-info';
import {Profile} from 'feedback-system/dto/IProfile';
import {
    getSearchUsersAction
} from './services/actions';

export interface OwnProps {
    endpoint: string;
    onUserChange: (user: Profile) => void;
}

interface Dispatch {
    getSearchUsersAction: typeof getSearchUsersAction;
}

interface State {
    name: string;
    users: Profile[];
    error: boolean;
}

interface Options {
    children: {[key: string]: any};
    containerProps: {[key: string]: any};
    query: string;
}

type IProps = Classes & OwnProps & Dispatch;

const getSuggestions = (users: Profile[], value: string) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;
    return inputLength === 0
        ? []
        : users.filter((user: Profile) => {
            const userName = (`${user.firstName} ${user.lastName}`).toLowerCase();
            return userName.includes(inputValue);
        });
};

const renderInputComponent = (inputProps: any) => {
    const {
        classes,
        inputRef = () => {
            return false;
        },
        ref,
        ...other
    } = inputProps;
    return (
        <TextField
            fullWidth={true}
            InputProps={{
                inputRef: node => {
                    ref(node);
                    inputRef(node);
                },
                classes: {
                    input: classes.input
                }
            }}
            {...other}
        />
    );
};

const renderSuggestion = (
    user: Profile
) => {
    return (
        <MenuItem component="div">
            <UserInfo user={user} />
        </MenuItem>
    );
};

const getSuggestionValue = (user: Profile) => {
    return `${user.firstName} ${user.lastName}`;
};

const renderSuggestionsContainer = (options: Options) => (
    <Paper {...options.containerProps} square={true} style={{maxHeight: 320, overflow: 'auto'}}>
        {options.children}
    </Paper>
);

const renderServerError = () => (
    <Paper square={true} style={{maxHeight: 320, overflow: 'auto'}}>
        <Typography variant={'h6'}>
            Server error
        </Typography>
    </Paper>
);

const SearchUser = (props: IProps) => {

    const {classes, onUserChange} = props;

    const [stateSuggestions, setSuggestions] = useState<Profile[]>([]);
    const [state, setState] = useState<State>({
        name: '',
        users: [],
        error: false
    });

    const getUsers = (users: Profile[]) => {
        setState({
            ...state,
            users,
            error: false
        });
    };

    const setError = (users: Profile[]) => {
        setState({
            ...state,
            error: true
        });
    };

    useEffect(() => {
        if (!state.users.length) {
            props.getSearchUsersAction(props.endpoint, getUsers, setError);
        }
    });

    const handleSuggestionsFetchRequested = ({value}: any) => {
        setSuggestions(getSuggestions(state.users, value));
    };

    const handleSuggestionsClearRequested = () => {
        setSuggestions([]);
    };

    const handleChange = (name: string) => (
        event: React.ChangeEvent<{}>,
        {newValue}: Autosuggest.ChangeEvent
    ) => {
        setState({
            ...state,
            [name]: newValue
        });
    };

    const handleSuggestionSelected = (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        {suggestion}: {suggestion: Profile}) => {
        onUserChange(suggestion);
    };

    const autosuggestProps = {
        renderInputComponent,
        suggestions: stateSuggestions,
        onSuggestionsFetchRequested: handleSuggestionsFetchRequested,
        onSuggestionsClearRequested: handleSuggestionsClearRequested,
        onSuggestionSelected: handleSuggestionSelected,
        getSuggestionValue,
        renderSuggestion
    };

    return (
        <>
            <Autosuggest
                {...autosuggestProps}
                inputProps={{
                    classes,
                    label: 'Employee',
                    placeholder: 'Enter name',
                    value: state.name,
                    onChange: handleChange('name')
                }}
                theme={{
                    container: classes.container,
                    suggestionsContainerOpen: classes.suggestionsContainerOpen,
                    suggestionsList: classes.suggestionsList,
                    suggestion: classes.suggestion
                }}
                renderSuggestionsContainer={!state.error ? renderSuggestionsContainer : renderServerError}
            />
        </>
    );
};

const mapDispatchToProps: Dispatch = {
    getSearchUsersAction
};

const connector = connect(null, mapDispatchToProps);

export default withStyles(styles)(connector(SearchUser));
